import { useState } from 'react';

/**
 * BUILD-043 Vendors - live field checks for phone, email and website. The SAME rules the Vendor Directory server enforces (model.js), so a person sees the problem
 * the moment they leave the field (a red box and a plain message under it) and the Save / Add button stays off until it is fixed - nothing is sent to be refused.
 * Blank is always allowed (these fields are optional). A value that is ALREADY SAVED is never re-judged unless it is edited.
 */
export type FieldKind = 'phone' | 'email' | 'website';

export const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
export const WEBSITE_RE = /^(https?:\/\/)?([A-Za-z0-9-]+\.)+[A-Za-z]{2,}(:\d+)?(\/\S*)?$/;

const digitsOf = (s: string) => s.replace(/\D/g, '');
/** The 10 digits of a US number (a leading 1 is dropped), or null when it is not one. */
export function usPhoneDigits(raw: string | null | undefined): string | null {
  const s = String(raw ?? '').trim();
  if (!s || !/^[0-9()+\-.\s]+$/.test(s)) return null;
  let d = digitsOf(s);
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d.length === 10 ? d : null;
}
/** (480) 555-0123 for a valid US number; anything else is returned exactly as typed. */
export function formatUsPhone(raw: string | null | undefined): string {
  const d = usPhoneDigits(raw);
  return d ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(raw ?? '');
}
/** Same phone, ignoring punctuation (so a saved "602-555-0111" and a re-typed "(602) 555-0111" are not seen as a change). */
export const samePhone = (a: string | null | undefined, b: string | null | undefined) => {
  const x = String(a ?? '').trim(); const y = String(b ?? '').trim();
  return x === y || (!!usPhoneDigits(x) && usPhoneDigits(x) === usPhoneDigits(y));
};

/** A ZIP code is exactly 5 digits, or 9 digits (12345-6789 or 123456789). Anything else (6 digits, letters, too short) is wrong. `saved` = the stored value, accepted untouched. */
export function postalProblem(postal: string | null | undefined, saved?: string | null): string | null {
  const v = String(postal ?? '').trim();
  if (!v) return null;
  if (saved != null && v === String(saved).trim()) return null;
  return /^\d{5}$/.test(v) || /^\d{5}-?\d{4}$/.test(v) ? null : 'ZIP code must be 5 digits (like 85001) or 9 digits (like 85001-1234).';
}
/** 9 digits typed together become 12345-6789; everything else is left as typed. */
export const formatPostal = (postal: string) => (/^\d{9}$/.test(postal.trim()) ? `${postal.trim().slice(0, 5)}-${postal.trim().slice(5)}` : postal);
/** Show the problem straight away (before leaving the field) once a ZIP is clearly wrong: letters, more digits than a ZIP can have, or 6-8 digits typed with no hyphen. */
export const postalObviouslyWrong = (postal: string) => /[^0-9-]/.test(postal) || postal.replace(/\D/g, '').length > 9 || /^\d{6,8}$/.test(postal);

/** The problem with a typed value, or null when it is fine. `saved` = the value already stored (accepted untouched). */
export function fieldProblem(kind: FieldKind, value: string, saved?: string | null): string | null {
  const v = (value || '').trim();
  if (!v) return null;
  if (saved != null && (v === String(saved).trim() || (kind === 'phone' && samePhone(v, saved)))) return null;
  if (kind === 'phone') {
    if (/[A-Za-z]/.test(v)) return 'Use digits only, like (480) 555-0123.';
    if (!/^[0-9()+\-.\s]+$/.test(v)) return 'Use digits only (spaces, dashes and parentheses are fine).';
    return usPhoneDigits(v) ? null : 'Enter a 10-digit US phone number, like (480) 555-0123.';
  }
  if (kind === 'email') return EMAIL_RE.test(v) ? null : 'Enter a full email address, like name@company.com.';
  return WEBSITE_RE.test(v) ? null : 'Enter a web address, like example.com or https://example.com.';
}

/**
 * A text box that checks itself. The red box + message show as soon as the person leaves the field (or immediately for letters in a phone number); a good phone number
 * is tidied to (480) 555-0123 on the way out. The form's button reads `fieldProblem` too, so it stays disabled while any of these is wrong.
 */
export function CheckedInput(p: {
  id: string; testid: string; kind: FieldKind; value: string; onChange: (v: string) => void; className: string; saved?: string | null; maxLength?: number; placeholder?: string;
}) {
  const [touched, setTouched] = useState(() => !!fieldProblem(p.kind, p.value, p.saved)); // an already-stored bad value is flagged the moment the form opens
  const problem = fieldProblem(p.kind, p.value, p.saved);
  const show = !!problem && (touched || (p.kind === 'phone' && /[A-Za-z]/.test(p.value)));
  return (
    <>
      <input
        id={p.id} data-testid={p.testid} type="text" inputMode={p.kind === 'phone' ? 'tel' : p.kind === 'email' ? 'email' : 'url'} autoComplete="off" maxLength={p.maxLength} placeholder={p.placeholder}
        className={`${p.className} ${show ? '!border-red-500 ring-1 ring-red-500 bg-red-50' : ''}`} value={p.value} aria-invalid={show} aria-describedby={show ? `${p.id}-error` : undefined}
        onChange={(e) => p.onChange(e.target.value)}
        onBlur={() => { setTouched(true); if (p.kind === 'phone' && p.value.trim() && !problem) p.onChange(formatUsPhone(p.value)); }}
      />
      {show && <p id={`${p.id}-error`} role="alert" data-testid={`${p.testid}-error`} className="mt-0.5 text-xs text-red-700">{problem}</p>}
    </>
  );
}
